import axios from "axios";
import Cookies from "js-cookie";
import { clearTaskListColumnsCache } from "./Task";
import { withApiErrorResponse, apiErrorMessage } from "../utils/apiError";

const baseURL = process.env.REACT_APP_BASE_BACKEND_API_URL;
const SUCCESS_CODE = 600;

const unwrapColumnAccessResponse = (response, fallbackMessage) => {
  const data = response?.data || {};
  const code = data?.header?.code;
  if (code !== SUCCESS_CODE) {
    const message = apiErrorMessage(data, fallbackMessage || "Request failed");
    const error = new Error(message);
    error.response = { data };
    throw withApiErrorResponse(error);
  }
  return data?.body?.value;
};

/**
 * List saved column-access grants.
 * GET api/v1/task/list/columns/access — workflowId/userType are both optional filters.
 */
export const getColumnAccessList = async ({ workflowId, userType } = {}) => {
  const token = Cookies.get("accessToken");
  const params = new URLSearchParams();
  if (workflowId !== undefined && workflowId !== null && workflowId !== "") {
    params.append("workflowId", workflowId);
  }
  if (userType) {
    params.append("userType", userType);
  }
  const query = params.toString();
  try {
    const response = await axios.get(
      `${baseURL}api/v1/task/list/columns/access${query ? `?${query}` : ""}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return unwrapColumnAccessResponse(response, "Error fetching column access list") ?? [];
  } catch (error) {
    console.error("Error fetching column access list:", error);
    throw withApiErrorResponse(error);
  }
};

/**
 * List workflow columns that are valid for access grants.
 * GET api/v1/task/list/columns?workflowIds=:workflowId&userType=:userType
 */
export const getColumnOptionsForAccess = async ({ workflowId, userType } = {}) => {
  const token = Cookies.get("accessToken");
  const params = new URLSearchParams();
  if (workflowId !== undefined && workflowId !== null && workflowId !== "") {
    params.append("workflowIds", workflowId);
  }
  if (userType) {
    params.append("userType", userType);
  }
  const query = params.toString();
  try {
    const response = await axios.get(
      `${baseURL}api/v1/task/list/columns${query ? `?${query}` : ""}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return unwrapColumnAccessResponse(response, "Error fetching workflow columns") ?? [];
  } catch (error) {
    console.error("Error fetching workflow columns for access:", error);
    throw withApiErrorResponse(error);
  }
};

/**
 * Grant column access (super_admin only). columnKeys is the complete allowed
 * set for the given workflow/userType and replaces any previous grant.
 * POST api/v1/task/list/columns/access
 * Body: { workflowId, userTypes: [userType], columnKeys }
 */
export const grantColumnAccess = async ({ workflowId, userType, userTypes, columnKeys }) => {
  const token = Cookies.get("accessToken");
  const normalizedUserTypes = Array.isArray(userTypes)
    ? userTypes
    : userType
      ? [userType]
      : [];
  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/list/columns/access`,
      { workflowId, userTypes: normalizedUserTypes, columnKeys },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    // Grants changed, so any cached /list/columns response is now stale.
    clearTaskListColumnsCache();
    return unwrapColumnAccessResponse(response, "Error granting column access") ?? [];
  } catch (error) {
    console.error("Error granting column access:", error);
    throw withApiErrorResponse(error);
  }
};

/**
 * Update an existing grant's columnKeys (replaces the whole set).
 * PUT api/v1/task/list/columns/access/:taskColumnAccessId
 * Body: { columnKeys }
 */
export const updateColumnAccess = async (taskColumnAccessId, columnKeys) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.put(
      `${baseURL}api/v1/task/list/columns/access/${taskColumnAccessId}`,
      { columnKeys },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    clearTaskListColumnsCache();
    return unwrapColumnAccessResponse(response, "Error updating column access") ?? null;
  } catch (error) {
    console.error("Error updating column access:", error);
    throw withApiErrorResponse(error);
  }
};

/**
 * Remove a grant entirely.
 * DELETE api/v1/task/list/columns/access/:taskColumnAccessId
 */
export const deleteColumnAccess = async (taskColumnAccessId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.delete(
      `${baseURL}api/v1/task/list/columns/access/${taskColumnAccessId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    clearTaskListColumnsCache();
    return unwrapColumnAccessResponse(response, "Error deleting column access");
  } catch (error) {
    console.error("Error deleting column access:", error);
    throw withApiErrorResponse(error);
  }
};
